import NiceModal from "@ebay/nice-modal-react";
import { Input } from "antd";
import * as React from "react";
import { AddButton, ColumnType, PhoneColumn, Table } from "src/components";
import { BlacklistItem, useBlacklistBankAccounts, useBlacklistPhones } from "src/hooks";
import { datetime } from "src/utils";
import { BlacklistFormModal, BlacklistFormType } from "./BlacklistFormModal";

export type BlacklistTableProps = {
  type: BlacklistFormType;
};

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"]; // backend max per_page is 100
const SEARCH_DEBOUNCE_MS = 400;

const labelByType: Record<BlacklistFormType, string> = {
  phone: "เบอร์โทร",
  bankAccount: "ชื่อบัญชี",
};

export const BlacklistTable = ({ type }: BlacklistTableProps) => {
  const label = labelByType[type];

  const [searchText, setSearchText] = React.useState("");
  const [search, setSearch] = React.useState(""); // debounced value sent to the API
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const query = { page, perPage: pageSize, search };
  const phonesQuery = useBlacklistPhones(query, type === "phone");
  const bankAccountsQuery = useBlacklistBankAccounts(query, type === "bankAccount");
  const { data, isFetching, isError } = type === "phone" ? phonesQuery : bankAccountsQuery;

  const total = data?.total ?? 0;

  // Current page became empty (e.g. last row deleted) -> step back one page.
  React.useEffect(() => {
    if (!isFetching && !isError && data && data.results.length === 0 && page > 1) {
      setPage(Math.max(1, Math.min(page - 1, Math.ceil(total / pageSize) || 1)));
    }
  }, [data, isFetching, isError, page, pageSize, total]);

  const filterKey = type === "phone" ? "phone" : "bank_account_name";

  const columns: ColumnType<BlacklistItem>[] = [
    {
      title: label,
      key: "value",
      dataIndex: filterKey,
      width: "20%",
      render: (value: string | null) =>
        type === "phone" ? <PhoneColumn phoneNumber={value || ""} /> : value || "-",
    },
    {
      // Free text from CORE, up to a few hundred characters. Truncated with
      // `ellipsis` and given a native title tooltip so one long reason can't
      // stretch every row — the full text is still readable on hover.
      title: "เหตุผล",
      key: "reason",
      dataIndex: "reason",
      width: "32%",
      ellipsis: true,
      render: (value: string | null) => <span title={value || ""}>{value || "-"}</span>,
    },
    {
      title: "วันเวลาอัปเดตล่าสุด",
      key: "created_at",
      dataIndex: "created_at",
      width: "24%",
      render: (value: string | null) =>
        value && datetime(value).isValid() ? datetime(value).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      title: "อัปเดตโดย",
      key: "actor_name",
      dataIndex: "actor_name",
      width: "24%",
      render: (value: string | null) => value || "-",
    },
  ];

  const operations = (
    <AddButton key="add" onClick={() => NiceModal.show(BlacklistFormModal, { type })}>
      เพิ่มรายการ
    </AddButton>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Input
          size="large"
          placeholder={`ค้นหา${label}`}
          allowClear
          style={{ width: 280 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {operations}
      </div>
      <Table
        rowKey="uid"
        columns={columns}
        dataSource={isError && !data ? undefined : data?.results}
        keepShowTable={page > 1}
        pagination={{
          current: page,
          pageSize,
          total,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onChange: (nextPage, nextSize) => {
            if (nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          },
        }}
        scroll={undefined}
        loading={{
          size: "large",
          spinning: isFetching,
          style: { maxHeight: "calc(100vh - 310px)" },
        }}
      />
    </div>
  );
};

export default BlacklistTable;
